import React, { useState } from 'react';
import { useToasts } from 'Context';
import { ConfirmDialog } from 'v3/Components/Shared/ConfirmDialog';
import ReadOnlyField from 'v3/Components/Form/ReadOnlyField';
import TextInput from 'v3/Components/Form/TextInput';
import { ReviewerLifecycleStatus } from 'v3/Models/Enums';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { buildEndpoint, useDataPoster } from 'Utilities';
import { getMissingSectionsFromErrorResponse, MissingSectionError } from './ReviewerProfile.ProfileCompleteness';
import './ReviewerProfile.InviteDialog.scss';

const DEFAULT_AUDIT_HOLD_CASE_COUNT = '2';

// STRAD2-2319 explicitly excludes the Audit Hold case-count section from this modal — it's Phase 4
// scope. Gated behind this flag (rather than deleted) so it's a one-line flip to bring back.
const SHOW_AUDIT_HOLD_SECTION = false;

type ReviewerProfileInviteDialogProps = {
  reviewerId: string;
  email?: string;
  dialogVisible: boolean;
  toggleDialog: () => void;
  onSuccess: () => void;
  onValidationError?: (missingSections: MissingSectionError[]) => void;
};

const ReviewerProfileInviteDialog: React.FC<ReviewerProfileInviteDialogProps> = ({
  reviewerId,
  email,
  dialogVisible,
  toggleDialog,
  onSuccess,
  onValidationError,
}) => {
  const toast = useToasts();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [auditHoldCaseCount, setAuditHoldCaseCount] = useState(DEFAULT_AUDIT_HOLD_CASE_COUNT);

  const [postData] = useDataPoster(
    buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'status'),
    undefined,
    true,
    '3.0'
  );

  const handleClose = () => {
    setAuditHoldCaseCount(DEFAULT_AUDIT_HOLD_CASE_COUNT);
    toggleDialog();
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const resp: any = await postData({ newStatus: { value: ReviewerLifecycleStatus.Invited } });
      if (resp?.status === 200) {
        toast.success('Reviewer has been invited');
        onSuccess();
        handleClose();
      } else if (resp?.response?.status === 400) {
        const missingSections = getMissingSectionsFromErrorResponse(resp);
        if (missingSections) {
          toast.error('Please complete all required fields before inviting this reviewer.');
          onValidationError?.(missingSections);
          handleClose();
        } else {
          toast.error(resp.response?.data?.message ?? 'Failed to invite reviewer. Please try again.');
        }
      } else if (resp?.response?.status === 403) {
        toast.error(resp.response?.data?.message ?? 'You do not have permission to perform this action.');
      } else if ((resp?.response?.status ?? 0) >= 500) {
        toast.error('Failed to invite reviewer. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ConfirmDialog
      dialogVisible={dialogVisible}
      toggleDialog={handleClose}
      className="invite-reviewer-dialog"
      title="Invite Reviewer"
      primaryButtonText={isSubmitting ? 'Inviting...' : 'Invite'}
      primaryButtonEnabled={!isSubmitting}
      OnPrimaryButtonClick={handleConfirm}
      secondaryButtonText="Cancel"
      OnSecondaryButtonClick={handleClose}
    >
      <p className="invite-reviewer-dialog__description">
        Please confirm you are ready to invite this reviewer to the system? Doing so will send the reviewer an
        email with a link to join Stradix. Review the email address to ensure the invite link will be sent to
        the correct address.
      </p>
      <ReadOnlyField forceLabel="Email" value={email} />

      {SHOW_AUDIT_HOLD_SECTION && (
        <div className="invite-reviewer-dialog__audit-hold">
          <h3 className="invite-reviewer-dialog__audit-hold-title">Confirm Audit Hold</h3>
          <p className="invite-reviewer-dialog__description">
            Since this is a new reviewer, they will automatically be placed on audit hold when they are active.
            Confirm how many cases the reviewer should be placed on audit hold for. The default number is set to
            two.
          </p>
          <TextInput
            label="Number of Cases on Audit Hold"
            required
            type="number"
            min={0}
            value={auditHoldCaseCount}
            onChange={(e) => setAuditHoldCaseCount(e.target.value)}
          />
        </div>
      )}
    </ConfirmDialog>
  );
};

export default ReviewerProfileInviteDialog;
